'use client';

import { FC, Fragment, ReactNode } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Button, Heading } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { arrowRight } from '@purpur/library/icon/assets/arrow-right';

import { ButtonLink } from '@telialt/common-button-link/src';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';

import { BlockCtaType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { ImageLink } from '../common/ImageLink';
import { CtaProvider } from '../CtaProvider/CtaProvider';
import {
    ButtonsWrapper,
    DescriptionWrapper,
    SplitContentWrapper,
    StyledBlockCta,
    StyledBlockCtaContainer,
    TitleWrapper,
} from './BlockCta.styles';
import { BlockCtaData } from './model';
import { transformBlockCta } from './transformers';

type Props = {
    contentItem: BlockCtaType;
    contained?: boolean;
};

const BlockCtaButtons: FC<{ block: BlockCtaData; showIcon?: boolean }> = ({ block, showIcon }) => (
    <ButtonsWrapper>
        {block.cta.map((cta, index) => (
            <Fragment key={cta.id}>
                {cta.image ? (
                    <ImageLink cta={cta} dataTest={`${block.id}-${cta.id}`} />
                ) : (
                    <CtaProvider cta={cta}>
                        {({ id, href, target, rel, text, onClick }) => {
                            const variant = index === 0 ? 'primary' : 'secondary';
                            const label = (
                                <>
                                    {text}
                                    {showIcon && <Icon svg={arrowRight} size="xs" />}
                                </>
                            );

                            if (href) {
                                return (
                                    <ButtonLink
                                        variant={variant}
                                        dataTest={`${block.id}-${id}`}
                                        href={href}
                                        target={target}
                                        rel={rel}
                                        onClick={onClick}
                                    >
                                        {label}
                                    </ButtonLink>
                                );
                            }

                            return (
                                <Button variant={variant} data-test={`${block.id}-${id}`} onClick={onClick}>
                                    {label}
                                </Button>
                            );
                        }}
                    </CtaProvider>
                )}
            </Fragment>
        ))}
    </ButtonsWrapper>
);

export const BlockCta: FC<Props> = ({ contentItem: initialContentItem, contained }) => {
    const contentItem = useContentfulLiveUpdates(initialContentItem);
    const block = transformBlockCta(contentItem);

    const Comp = contained ? StyledBlockCtaContainer : Fragment;
    const hasContent = !!block.description || !!block.title;
    const isCentered = block.displayOption === 'centered';
    const isSplit = block.displayOption === 'split';
    const isLightPurple = block.colorOption === 'color-light-purple';
    const headingVariant = isLightPurple ? 'title-300' : 'title-200';
    const showIcon = isSplit && isLightPurple;

    const title: ReactNode = !!block.title && (
        <Heading tag="h2" variant={headingVariant}>
            {block.title}
        </Heading>
    );

    const description: ReactNode = !!block.description && <RichTextContent richText={block.description} />;

    return (
        <Comp>
            <StyledBlockCta
                $boxed={hasContent}
                $centered={isCentered}
                $split={isSplit}
                $colorOption={block.colorOption}
                id={getBlockId(contentItem)}
                data-test="block-cta"
                data-display={block.displayOption || 'default'}
                data-color={block.colorOption || 'default'}
            >
                {isSplit ? (
                    <>
                        {!!block.title && <TitleWrapper>{title}</TitleWrapper>}
                        <SplitContentWrapper>
                            {description}
                            <BlockCtaButtons block={block} showIcon={showIcon} />
                        </SplitContentWrapper>
                    </>
                ) : (
                    <>
                        {hasContent && (
                            <DescriptionWrapper>
                                {title}
                                {description}
                            </DescriptionWrapper>
                        )}
                        <BlockCtaButtons block={block} />
                    </>
                )}
            </StyledBlockCta>
        </Comp>
    );
};
